import { useEffect, useMemo, useRef, useState } from "react";
import { Plus, ArrowLeft, Upload, CheckCircle2, X, Trash2, Download } from "lucide-react";
import { toast } from "sonner";
import { API, getEmployeeToken, getToken } from "@/lib/api";

/**
 * Shared Checkouts page — admin and employee.
 * Props:
 *   mode: "admin" | "employee"
 *   apiClient: axios instance with correct auth
 */
export default function CheckoutsPage({ mode = "employee", apiClient }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("out"); // out / returned / all
  const [creating, setCreating] = useState(false);
  const [detail, setDetail] = useState(null); // checkout id to view/check-in

  async function load() {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/checkouts");
      setRows(data || []);
    } catch { toast.error("Could not load checkouts"); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  async function removeRow(id) {
    if (mode !== "admin") return;
    if (!window.confirm("Delete this checkout record?")) return;
    try { await apiClient.delete(`/checkouts/${id}`); load(); toast.success("Deleted"); }
    catch { toast.error("Failed to delete"); }
  }

  async function downloadPdf(id, isReturned, e) {
    e?.stopPropagation?.();
    try {
      const res = await apiClient.get(`/checkouts/${id}/sheet.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${isReturned ? "checkin" : "checkout"}-${id.slice(0,8)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("PDF download failed"); }
  }

  const filtered = useMemo(() => {
    if (tab === "all") return rows;
    return rows.filter((r) => r.status === tab);
  }, [rows, tab]);

  return (
    <div data-testid={`${mode}-checkouts-page`}>
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#C9963F] mb-2">Catering</div>
          <h1 className="font-serif-brand text-4xl sm:text-5xl tracking-tight">Check-out / Check-in</h1>
          <p className="text-white/50 mt-2 text-sm">{rows.length} record{rows.length !== 1 ? "s" : ""}</p>
        </div>
        <div className="flex items-center gap-2">
          {["out", "returned", "all"].map((s) => (
            <button key={s} onClick={() => setTab(s)} data-testid={`co-tab-${s}`}
              className={`text-[10px] uppercase tracking-wider px-3 py-2 border ${tab === s ? "border-[#C9963F] text-[#C9963F] bg-[#C9963F]/10" : "border-white/15 text-white/70 hover:border-white/40"}`}>
              {s === "out" ? "Out" : s === "returned" ? "Returned" : "All"}
            </button>
          ))}
          <button onClick={() => setCreating(true)} data-testid="co-new-btn"
            className="inline-flex items-center gap-2 bg-[#C9963F] text-black px-4 py-2 text-sm font-medium hover:bg-[#A67A2D]">
            <Plus size={14} /> New Check-out
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-white/40 text-sm">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="bg-[#0e0e0e] border border-white/10 p-8 text-center text-white/40 text-sm">
          No checkouts yet. Click <span className="text-[#C9963F]">New Check-out</span> to start one.
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((r) => (
            <button
              key={r.id}
              onClick={() => setDetail(r.id)}
              data-testid={`co-row-${r.id}`}
              className="w-full text-left bg-[#0e0e0e] border border-white/10 hover:border-[#C9963F]/50 px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4"
            >
              <div className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider px-2 py-0.5 border ${r.status === "returned" ? "border-[#C9963F]/40 text-[#C9963F]" : "border-amber-300/40 text-amber-300"}`}>
                {r.status === "returned" ? <CheckCircle2 size={10} /> : null}
                {r.status === "returned" ? "Returned" : "Out"}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-white text-sm font-medium truncate">{r.client_name || "—"} <span className="text-white/40 text-xs">· {r.event_date}</span></div>
                <div className="text-[11px] text-white/50 truncate">{r.items?.map((i) => `${i.name}×${i.qty}`).join(", ")}</div>
              </div>
              <div className="text-[10px] uppercase tracking-wider text-white/40 sm:text-right">
                {r.employee_name} · {new Date(r.created_at).toLocaleDateString()}
              </div>
              <button
                onClick={(e) => downloadPdf(r.id, r.status === "returned", e)}
                data-testid={`co-pdf-${r.id}`}
                className="p-1.5 text-white/50 hover:text-[#C9963F]"
                title="Download PDF"
              ><Download size={14} /></button>
              {mode === "admin" && (
                <button
                  onClick={(e) => { e.stopPropagation(); removeRow(r.id); }}
                  data-testid={`co-delete-${r.id}`}
                  className="p-1.5 text-white/40 hover:text-red-400"
                ><Trash2 size={14} /></button>
              )}
            </button>
          ))}
        </div>
      )}

      {creating && (
        <NewCheckoutDialog
          apiClient={apiClient}
          onCancel={() => setCreating(false)}
          onSaved={() => { setCreating(false); load(); }}
        />
      )}

      {detail && (
        <CheckoutDetailDialog
          apiClient={apiClient}
          mode={mode}
          id={detail}
          onClose={() => { setDetail(null); load(); }}
        />
      )}
    </div>
  );
}

function NewCheckoutDialog({ apiClient, onCancel, onSaved }) {
  const [events, setEvents] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [eventId, setEventId] = useState("");
  const [picks, setPicks] = useState({}); // {item_id: qty}
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        // Try the employee-only endpoint first (no pricing); fall back to admin
        // contracts list ONLY if we're using the admin client.
        let evs;
        try {
          evs = await apiClient.get("/employee/events-upcoming");
        } catch {
          // Employee endpoint not authorised for admin tokens — use admin contracts list.
          evs = await apiClient.get("/contracts");
        }
        const inv = await apiClient.get("/inventory");
        setEvents((evs.data || []).map((e) => ({
          id: e.id, client_name: e.client_name, event_date: e.event_date,
          event_time: e.event_time || "", event_location: e.event_location || ""
        })));
        setInventory(inv.data || []);
      } catch { toast.error("Could not load events/inventory"); }
    })();
  }, [apiClient]);

  function setQty(id, val) {
    const n = Math.max(0, Number(val) || 0);
    setPicks((p) => ({ ...p, [id]: n }));
  }

  async function save() {
    if (!eventId) return toast.error("Pick an event");
    const items = Object.entries(picks)
      .filter(([, q]) => q > 0)
      .map(([item_id, qty]) => ({ item_id, qty }));
    if (!items.length) return toast.error("Select at least one item");
    setSaving(true);
    try {
      await apiClient.post("/checkouts", { contract_id: eventId, items });
      toast.success("Check-out created");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create");
    } finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={onCancel}>
      <div className="bg-[#0e0e0e] border border-[#C9963F]/40 max-w-2xl w-full p-6 sm:p-8 relative my-8" onClick={(e) => e.stopPropagation()} data-testid="co-new-dialog">
        <button onClick={onCancel} className="absolute top-4 right-4 text-white/50 hover:text-white"><X size={18} /></button>
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#C9963F] mb-2">New Check-out</div>
        <h3 className="font-serif-brand text-2xl mb-5">Select event & items</h3>

        <label className="block text-[10px] uppercase tracking-[0.2em] text-white/50 mb-2">Event *</label>
        <select
          value={eventId}
          onChange={(e) => setEventId(e.target.value)}
          data-testid="co-event-select"
          className="w-full bg-[#0a0a0a] border border-white/10 px-3 py-2 text-sm focus:border-[#C9963F] focus:outline-none text-white mb-5"
        >
          <option value="">— Pick an upcoming event —</option>
          {events.map((e) => (
            <option key={e.id} value={e.id}>
              {e.event_date} — {e.client_name}{e.event_location ? ` @ ${e.event_location}` : ""}
            </option>
          ))}
        </select>

        <label className="block text-[10px] uppercase tracking-[0.2em] text-white/50 mb-2">Items to take out</label>
        <div className="bg-[#0a0a0a] border border-white/10 max-h-80 overflow-y-auto divide-y divide-white/5" data-testid="co-items-picker">
          {inventory.length === 0 && <div className="p-4 text-white/40 text-xs">No inventory items yet — add some on the Inventory page first.</div>}
          {inventory.map((it) => (
            <div key={it.id} className="flex items-center gap-3 p-3" data-testid={`co-pick-${it.id}`}>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-white truncate">{it.name}</div>
                <div className="text-[10px] uppercase tracking-wider text-white/40">{it.section} · {it.available_quantity}/{it.quantity} available</div>
              </div>
              <input
                type="number"
                min="0"
                max={it.available_quantity}
                value={picks[it.id] ?? ""}
                onChange={(e) => setQty(it.id, e.target.value)}
                data-testid={`co-pick-qty-${it.id}`}
                placeholder="0"
                className="w-20 bg-transparent border border-white/15 px-2 py-1 text-sm text-white text-right focus:border-[#C9963F] focus:outline-none"
              />
            </div>
          ))}
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <button onClick={onCancel} className="text-white/60 hover:text-white px-4 py-2 text-sm">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="co-create-btn" className="bg-[#C9963F] text-black font-medium px-5 py-2 hover:bg-[#A67A2D] text-sm disabled:opacity-50">
            {saving ? "Saving…" : "Check Out"}
          </button>
        </div>
      </div>
    </div>
  );
}

function CheckoutDetailDialog({ apiClient, mode, id, onClose }) {
  const [co, setCo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [returns, setReturns] = useState({}); // {item_id: {returned, damaged, missing, note}}
  const fileInputs = useRef({});
  const token = getEmployeeToken() || getToken();

  async function load() {
    try {
      const { data } = await apiClient.get(`/checkouts/${id}`);
      setCo(data);
      // Seed returns map from server values (default: all returned)
      const seed = {};
      for (const it of data.items || []) {
        seed[it.item_id] = {
          returned: it.returned_qty ?? it.qty,
          damaged: it.damaged_qty ?? 0,
          missing: it.missing_qty ?? 0,
          note: it.damage_note ?? "",
        };
      }
      setReturns(seed);
    } catch { toast.error("Could not load"); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  function photoFor(itemId) {
    return `${API}/checkouts/${id}/items/${itemId}/return-photo?auth=${encodeURIComponent(token || "")}`;
  }

  function setField(itemId, key, val) {
    setReturns((r) => ({ ...r, [itemId]: { ...(r[itemId] || {}), [key]: val } }));
  }

  async function uploadPhoto(itemId, file) {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await apiClient.post(`/checkouts/${id}/items/${itemId}/return-photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setCo(data);
      toast.success("Photo uploaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    }
  }

  async function checkin() {
    if (!co) return;
    const missingPhoto = co.items.filter((it) => !it.return_photo_path);
    if (missingPhoto.length) return toast.error(`Upload a return photo for: ${missingPhoto.map((m) => m.name).join(", ")}`);
    // Validate sums client-side
    for (const it of co.items) {
      const r = returns[it.item_id] || {};
      const sum = (Number(r.returned) || 0) + (Number(r.damaged) || 0) + (Number(r.missing) || 0);
      if (sum > it.qty) return toast.error(`${it.name}: returned+damaged+missing (${sum}) exceeds checked-out qty (${it.qty})`);
    }
    setBusy(true);
    try {
      await apiClient.post(`/checkouts/${id}/return`, {
        items: co.items.map((it) => {
          const r = returns[it.item_id] || {};
          return {
            item_id: it.item_id,
            returned_qty: Number(r.returned) || 0,
            damaged_qty: Number(r.damaged) || 0,
            missing_qty: Number(r.missing) || 0,
            damage_note: r.note || "",
          };
        }),
      });
      toast.success("Checked back in");
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Check-in failed");
    } finally { setBusy(false); }
  }

  if (!co) {
    return (
      <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
        <div className="bg-[#0e0e0e] border border-white/10 p-6 text-white/40 text-sm">Loading…</div>
      </div>
    );
  }

  const isReturned = co.status === "returned";
  const numCls = "w-16 bg-transparent border border-white/15 px-2 py-1 text-sm text-white text-right focus:border-[#C9963F] focus:outline-none";

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-[#0e0e0e] border border-[#C9963F]/40 max-w-2xl w-full p-6 sm:p-8 relative my-8" onClick={(e) => e.stopPropagation()} data-testid="co-detail-dialog">
        <button onClick={onClose} className="absolute top-4 right-4 text-white/50 hover:text-white"><X size={18} /></button>
        <button onClick={onClose} className="text-white/40 hover:text-white inline-flex items-center gap-1 text-xs mb-3"><ArrowLeft size={12} /> Back</button>
        <div className="flex items-start justify-between mb-2">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#C9963F]">{isReturned ? "Returned" : "Out"}</div>
          <button
            onClick={async () => {
              try {
                const res = await apiClient.get(`/checkouts/${id}/sheet.pdf`, { responseType: "blob" });
                const url = URL.createObjectURL(res.data);
                const a = document.createElement("a"); a.href = url;
                a.download = `${isReturned ? "checkin" : "checkout"}-${id.slice(0,8)}.pdf`;
                document.body.appendChild(a); a.click(); a.remove();
                URL.revokeObjectURL(url);
              } catch { toast.error("PDF download failed"); }
            }}
            data-testid="co-detail-pdf-btn"
            className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#C9963F] border border-[#C9963F]/40 px-2.5 py-1 hover:bg-[#C9963F]/10"
          >
            <Download size={12} /> PDF
          </button>
        </div>
        <h3 className="font-serif-brand text-2xl mb-1">{co.client_name}</h3>
        <div className="text-xs text-white/50 mb-5">
          {co.event_date} · {co.employee_name}
          {co.returned_at && ` · Returned ${new Date(co.returned_at).toLocaleDateString()} by ${co.returned_by_name || ""}`}
        </div>

        <div className="space-y-3">
          {co.items.map((it) => {
            const r = returns[it.item_id] || {};
            const ret = Number(r.returned) || 0;
            const dmg = Number(r.damaged) || 0;
            const mis = Number(r.missing) || 0;
            const sum = ret + dmg + mis;
            const overflow = sum > it.qty;
            return (
              <div key={it.item_id} className="bg-[#0a0a0a] border border-white/10 p-3" data-testid={`co-detail-item-${it.item_id}`}>
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <div className="text-white text-sm font-medium">{it.name}</div>
                    <div className="text-[10px] uppercase tracking-wider text-white/40">
                      {it.section} · Checked out {it.qty}
                      {isReturned && ` · Ret ${it.returned_qty}${it.damaged_qty ? ` · Dmg ${it.damaged_qty}` : ""}${it.missing_qty ? ` · Missing ${it.missing_qty}` : ""}`}
                    </div>
                  </div>
                  {it.return_photo_path && (
                    <span className="text-[10px] uppercase tracking-wider text-[#C9963F] inline-flex items-center gap-1"><CheckCircle2 size={10} /> Photo</span>
                  )}
                </div>

                <div className="flex items-start gap-3">
                  {it.return_photo_path ? (
                    <img src={photoFor(it.item_id)} alt="" className="h-24 w-24 object-cover border border-white/10 shrink-0" />
                  ) : (
                    <div className="h-24 w-24 border border-dashed border-white/15 flex items-center justify-center text-[10px] uppercase tracking-wider text-white/30 shrink-0">No photo</div>
                  )}

                  <div className="flex-1 min-w-0 space-y-2">
                    {!isReturned ? (
                      <>
                        <div className="flex flex-wrap items-center gap-3">
                          <button
                            type="button"
                            onClick={() => fileInputs.current[it.item_id]?.click()}
                            data-testid={`co-upload-photo-${it.item_id}`}
                            className="inline-flex items-center gap-2 border border-[#C9963F]/40 text-[#C9963F] hover:bg-[#C9963F]/10 px-3 py-1.5 text-xs"
                          >
                            <Upload size={12} /> {it.return_photo_path ? "Replace Photo" : "Upload Photo"}
                          </button>
                          <input
                            ref={(el) => { fileInputs.current[it.item_id] = el; }}
                            type="file" accept="image/*" capture="environment" hidden
                            onChange={(e) => uploadPhoto(it.item_id, e.target.files?.[0])}
                          />
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                          <label className="block">
                            <div className="text-[9px] uppercase tracking-wider text-white/40 mb-1">Returned</div>
                            <input type="number" min="0" max={it.qty} value={r.returned ?? it.qty} onChange={(e) => setField(it.item_id, "returned", Number(e.target.value))} data-testid={`co-returned-${it.item_id}`} className={numCls} />
                          </label>
                          <label className="block">
                            <div className="text-[9px] uppercase tracking-wider text-amber-300/80 mb-1">Damaged</div>
                            <input type="number" min="0" max={it.qty} value={r.damaged ?? 0} onChange={(e) => setField(it.item_id, "damaged", Number(e.target.value))} data-testid={`co-damaged-${it.item_id}`} className={numCls} />
                          </label>
                          <label className="block">
                            <div className="text-[9px] uppercase tracking-wider text-red-400/80 mb-1">Missing</div>
                            <input type="number" min="0" max={it.qty} value={r.missing ?? 0} onChange={(e) => setField(it.item_id, "missing", Number(e.target.value))} data-testid={`co-missing-${it.item_id}`} className={numCls} />
                          </label>
                        </div>
                        {(dmg > 0 || mis > 0) && (
                          <input
                            type="text"
                            placeholder="Damage / loss note (optional)"
                            value={r.note ?? ""}
                            onChange={(e) => setField(it.item_id, "note", e.target.value)}
                            data-testid={`co-note-${it.item_id}`}
                            className="w-full bg-transparent border-b border-white/15 px-0 py-1 text-xs text-white placeholder-white/30 focus:border-[#C9963F] focus:outline-none"
                          />
                        )}
                        <div className={`text-[10px] uppercase tracking-wider ${overflow ? "text-red-400" : "text-white/40"}`} data-testid={`co-sum-${it.item_id}`}>
                          {ret} + {dmg} + {mis} = {sum} / {it.qty}{overflow ? " — too many!" : ""}
                        </div>
                      </>
                    ) : (
                      it.damage_note && <div className="text-xs text-white/60 italic">"{it.damage_note}"</div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {!isReturned && (
          <div className="flex justify-end gap-3 mt-6">
            <button onClick={onClose} className="text-white/60 hover:text-white px-4 py-2 text-sm">Close</button>
            <button onClick={checkin} disabled={busy} data-testid="co-checkin-btn" className="bg-[#C9963F] text-black font-medium px-5 py-2 hover:bg-[#A67A2D] text-sm disabled:opacity-50">
              {busy ? "Checking in…" : "Mark All Returned"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
